/**
 * ISG Quality Platform — Main Entry Point
 */
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';
import './styles/quality-overrides.css';

import { router, store, auth , checkEnvBanner } from '@isg/ui-kit';
import { renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { applyBranding, loadBranding } from '@isg/ui-kit';
import { checkTestMode, renderTestBanner } from '@isg/ui-kit';
import { createNotificationBell, createNotificationSidebar } from './components/notification-sidebar.js';
import { t, initI18n, onLocaleChange } from './i18n/index.js';

/* SSO: capture handoff from intranet broker */
auth.captureSSOHandoff();

/**
 * Navigation configuration — Quality-specific.
 * To add a new page: add an entry to the relevant section's items array.
 * Built at RENDER time (never at module scope) so every label is t()'d in the
 * reader's locale.
 */
function buildNavConfig() {
  return [
    {
      section: null,
      items: [
        { path: '/', label: t('nav.overview'), icon: 'layout-dashboard' },
      ],
    },
    {
      section: t('nav.equipment'),
      items: [
        { path: '/equipment', label: t('nav.equipment_register'), icon: 'wrench' },
      ],
    },
    {
      section: t('nav.calibration'),
      items: [
        { path: '/calibrations', label: t('nav.schedule'), icon: 'calendar-check' },
        { path: '/certificates', label: t('nav.certificates'), icon: 'file-certificate' },
        { path: '/submissions', label: t('nav.submissions_review'), icon: 'clipboard-check' },
      ],
    },
    {
      section: t('nav.inspection'),
      items: [
        { path: '/inspections', label: t('nav.schedule'), icon: 'clipboard-check' },
      ],
    },
    {
      section: t('nav.servicing'),
      items: [
        { path: '/servicing', label: t('nav.schedule'), icon: 'wrench' },
      ],
    },
    {
      section: t('nav.reports'),
      items: [
        { path: '/calibration-report', label: t('nav.calibration_report'), icon: 'file-bar-chart' },
      ],
    },
    {
      section: t('nav.system'),
      items: [
        { path: '/documents', label: t('nav.documents'), icon: 'file-text', adminOnly: true },
        { path: '/hot-topics', label: t('nav.hot_topics'), icon: 'megaphone', adminOnly: true },
        { path: '/form-builder', label: t('nav.form_builder'), icon: 'edit', adminOnly: true },
        { path: '/admin', label: t('nav.admin'), icon: 'settings', adminOnly: true },
      ],
    },
  ];
}

// Lazy-load pages
const pages = {
  dashboard:        () => import('./pages/dashboard.js'),
  login:            () => import('./pages/login.js'),
  equipment:        () => import('./pages/equipment.js'),
  calibrations:     () => import('./pages/calibrations.js'),
  certificates:     () => import('./pages/certificates.js'),
  submissions:      () => import('./pages/form-review.js'),
  inspectionForm:   () => import('./public/inspection-form.js'),
  equipmentLanding: () => import('./public/equipment-landing.js'),
  inspections:      () => import('./pages/inspections.js'),
  servicing:        () => import('./pages/servicing.js'),
  calibrationReport: () => import('./pages/calibration-report.js'),
  documents:        () => import('./pages/documents.js'),
  hotTopics:        () => import('./pages/hot-topics.js'),
  admin:            () => import('./pages/admin.js'),
  formBuilder:      () => import('./pages/form-builder.js'),
};

/**
 * Render the portal shell: accent stripe + sidebar + main content area.
 */
function renderAppShell() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  // Accent stripe at top
  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: t('app.title') });
  const overlay = createSidebarOverlay();

  const sidebar = renderNavigation({
    navConfig: buildNavConfig(),
    appTitle: t('app.shortTitle'),
    fallbackInitial: 'Q',
    appSlug: 'quality',
  });

  // Main wrapper with toolbar
  const mainWrapper = document.createElement('div');
  mainWrapper.style.cssText = 'display:flex;flex-direction:column;flex:1;min-width:0;';

  const toolbar = document.createElement('div');
  toolbar.className = 'global-toolbar';
  toolbar.style.cssText = 'display:flex;align-items:center;justify-content:flex-end;padding:0 var(--space-4);height:48px;border-bottom:1px solid var(--border-secondary);background:var(--surface-primary);gap:var(--space-2);flex-shrink:0;';

  const bellBtn = createNotificationBell();
  toolbar.appendChild(bellBtn);

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  mainWrapper.appendChild(toolbar);
  mainWrapper.appendChild(main);

  const notifSidebar = createNotificationSidebar();

  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebar);
  layout.appendChild(mainWrapper);
  app.appendChild(layout);
  app.appendChild(notifSidebar);

}

/**
 * Render a page module into #main-content.
 */
async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:50vh">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const module = await loader();
    main.innerHTML = '';
    await module.render(main, params);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">${t('common.something_went_wrong')}</p>
          <p>${err.message || t('app.page_failed')}</p>
          <button class="btn btn-primary mt-4" onclick="location.reload()">${t('app.reload')}</button>
        </div>
      </div>
    `;
  }
}

/**
 * Placeholder page for routes not yet implemented.
 */
function placeholderPage(title) {
  return () => Promise.resolve({
    render(container) {
      container.innerHTML = `
        <div class="main-content-inner">
          <div class="page-header">
            <h1>${title}</h1>
            <p>${t('app.coming_soon')}</p>
          </div>
          <div class="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M12 6v6l4 2"/><circle cx="12" cy="12" r="10"/>
            </svg>
            <p>${t('app.under_construction')}</p>
          </div>
        </div>
      `;
    }
  });
}

/**
 * Initialize the application.
 */
async function init() {
  // Pre-load branding so sidebar logo is ready before shell renders
  await loadBranding().catch(() => {});

  // Public routes (no auth required)
  const publicPaths = ['/login', '/forms/'];
  const publicPatterns = [/^\/equipment\/\d+\/scan$/];

  // QR pages never check the session. checkSession() hands any browser holding
  // the estate SSO hint cookie to the broker, and on a warehouse PC signed in to
  // Microsoft that comes back silently as whoever owns the mailbox — usually no
  // quality grant, so /api/user/profile 403s and ui-kit replaces the page with
  // "Access Restricted" before the form loads (2026-09-17). The forms are
  // anonymous by design; see QR_PUBLIC_ENDPOINTS in app/__init__.py.
  const onQrPage = window.location.pathname.startsWith('/forms/')
    || publicPatterns.some(p => p.test(window.location.pathname));
  const hasSession = onQrPage ? false : await auth.checkSession();

  // Boot i18n with the signed-in user's stored locale BEFORE any shell or
  // page renders (signed out / QR page: null, which falls back to the
  // browser or default locale).
  await initI18n(hasSession ? store.get('user') : null).catch(() => {});

  // index.html's static <title> is English (painted before the locale is
  // known); from here on the tab is named in the reader's language.
  document.title = t('app.title');
  onLocaleChange(() => { document.title = t('app.title'); });

  // Define portal routes
  router
    .on('/', (params) => renderPage(pages.dashboard, params))
    .on('/equipment', (params) => renderPage(pages.equipment, params))
    .on('/equipment/:id', (params) => renderPage(pages.equipment, params))
    .on('/calibrations', (params) => renderPage(pages.calibrations, params))
    .on('/certificates', (params) => renderPage(pages.certificates, params))
    .on('/submissions', (params) => renderPage(pages.submissions, params))
    .on('/submissions/:id', (params) => renderPage(pages.submissions, params))
    .on('/inspections', (params) => renderPage(pages.inspections, params))
    .on('/servicing', (params) => renderPage(pages.servicing, params))
    .on('/equipment/:id/scan', (params) => renderPage(pages.equipmentLanding, params))
    .on('/forms/:slug', (params) => renderPage(pages.inspectionForm, params))
    .on('/calibration-report', (params) => renderPage(pages.calibrationReport, params))
    .on('/documents', (params) => renderPage(pages.documents, params))
    .on('/hot-topics', (params) => renderPage(pages.hotTopics, params))
    .on('/admin', (params) => renderPage(pages.admin, params))
    .on('/form-builder', (params) => renderPage(pages.formBuilder, params))
    .on('/login', (params) => renderPage(pages.login, params));

  // Auth guard
  router.before((path) => {
    // An AUTHENTICATED user must never be left on the login page. Waving
    // /login through unconditionally renders the login FORM inside the app
    // shell — the app looks signed in and signed out at once, and only a
    // manual reload clears it. Not an edge case: api.js bounces here on any
    // 401 while the httpOnly cookie usually survives, so the reload lands
    // authenticated; the SSO broker's ?next and plain bookmarks reach it too.
    // Fixed in isg-app-template 2026-07-18; back-propagated 2026-07-31.
    if (path === '/login' && store.get('isAuthenticated')) {
      router.navigate('/');
      return false;
    }
    if (publicPaths.some(p => path.startsWith(p))) return true;
    if (publicPatterns.some(p => p.test(path))) return true;
    if (!store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    return true;
  });

  const currentPath = window.location.pathname;
  const isPublicRoute = publicPaths.some(p => currentPath.startsWith(p)) || publicPatterns.some(p => p.test(currentPath));

  // Apply branding and test mode banner after every route change
  router.after(() => {
    setTimeout(() => {
      applyBranding();
      renderTestBanner();
    }, 50);
  });

  // Check test mode status (non-blocking)
  checkTestMode();

  if (!hasSession && isPublicRoute) {
    // Public route without login — render without app shell
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    await router.resolve();
    applyBranding();
    renderTestBanner();
  } else if (!hasSession) {
    // No session, not a public route — show login
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const module = await pages.login();
    module.render(document.getElementById('main-content'));
    if (currentPath !== '/login') {
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else {
    renderAppShell();
    checkEnvBanner(); // beta/test banner (no-op on live)
    await router.resolve();
    applyBranding();
    renderTestBanner();

    // Quality Assistant — floats on every page for authenticated users.
    const { renderAIAssistant } = await import('./components/ai-assistant-widget.js');
    const aiMount = document.createElement('div');
    aiMount.id = 'ai-assistant-mount';
    document.body.appendChild(aiMount);
    renderAIAssistant(aiMount);
  }
}

// Boot
init().catch((err) => {
  console.error('Failed to initialize app:', err);
  document.getElementById('app').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>${t('app.boot_failed')}</p>
      <button class="btn btn-primary" onclick="location.reload()">${t('app.reload')}</button>
    </div>
  `;
});
